import { Controller, FieldError, Control, FieldValues, FieldPath, PathValue, Path } from "react-hook-form";
import { AutocompleteView, ItemView } from "./styles";
import { CaretDown, X } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import React, { KeyboardEvent, ReactNode, useEffect, useId, useRef, useState } from "react";
import { OutsideClick } from "../../../OutSideClick";
import SimpleBar from "simplebar-react";
import { useApiProvider } from "../../../../providers/api/apiProvider";
import { ServiceReturn } from "../../../../types/ServiceResponse";
import { SpinLoading } from "../../../SpinLoading";
import { IconButton, Shape } from "../../../Buttons/IconButton";

export interface AsyncAutocompleteProps<T, P, C extends FieldValues> extends Omit<AutocompleteProps<T, P, C>, "name" | "error" | "value" | "onChange"> {
  name: FieldPath<C>;
  control: Control<C>;
};

export const AsyncAutocomplete = <DataType, SearchPayload, ControlFields extends FieldValues>({
  name,
  control,
  ...rest
}: AsyncAutocompleteProps<DataType, SearchPayload, ControlFields>) => {
  return (
    <Controller<ControlFields>
      name={ name }
      control={ control }
      render={({ field: { name, value, onChange }, fieldState: { error }}) => (
        <Autocomplete<DataType, SearchPayload, ControlFields>
          name={ name }
          value={ value }
          error={ error }
          onChange={ onChange }
          { ...rest }
        />
      )}
    />
  );
};

interface AutocompleteProps<T, P, C> {
  placeholder: string;
  label?: string;
  name: string;
  error?: FieldError;
  value: PathValue<C, Path<C>>;
  onChange: (...event: any[]) => void;
  onSearch: (params: P, token?: string) => Promise<ServiceReturn<Array<T & { id: string }>>>;
  renderItem: ( value: T ) => ReactNode;
  renderSelected: ( value: T ) => ReactNode;
  prefetchedSelectedOption?: (T & { id: string }) | null;
};

const Autocomplete = <DataType, SearchPayload, ControlFields>({
  label,
  placeholder,
  name,
  error,
  value,
  onChange,
  onSearch,
  renderItem,
  renderSelected,
  prefetchedSelectedOption,
}: AutocompleteProps<DataType, SearchPayload, ControlFields>) => {
  const generatedId = useId();
  const triggerRef = useRef<HTMLDivElement | null>( null );
  const searchInputRef = useRef<HTMLInputElement | null>( null );
  const labelId = `${ name }-${ generatedId }-label`;
  const optionsId = `${ name }-${ generatedId }-options`;
  const errorId = `${ name }-${ generatedId }-error`;
  const searchId = `${ name }-${ generatedId }-search`;
  const {
    simpleApiCall,
  } = useApiProvider();

  const [ open, setOpen ] = useState<boolean>( false );
  const [ loading, setLoading ] = useState<boolean>( false );

  const [ search, setSearch ] = useState<string>("");

  const [ options, setOptions ] = useState<Array<DataType & { id: string }>>([]);

  const [ selectedOption, setSelectedOption ] = useState<DataType | null>( null );

  const handleSearch = async ( search: string ) => {
    let options: Array<DataType & { id: string }> = [];

    if( search.length > 2 && !loading ) {
      options = await simpleApiCall<SearchPayload, Array<DataType & { id: string }>>( onSearch, { search } as SearchPayload, () => setLoading(( loading ) => !loading )) ?? [];

      setOptions( options ?? []);
    }

    return options;
  };

  const handleToClean = () => {
    setOptions([]);

    setSearch("");
  };

  const handleClearSelected = ( event: React.MouseEvent<HTMLButtonElement, MouseEvent> ) => {
    event.stopPropagation();

    setSelectedOption( null );

    onChange("");
  };

  const openAndFocusSearch = () => {
    setOpen( true );
    requestAnimationFrame(() => searchInputRef.current?.focus());
  };

  const handleTriggerKeyDown = ( event: KeyboardEvent<HTMLDivElement> ) => {
    if( event.key === "Enter" || event.key === " " || event.key === "ArrowDown" ) {
      event.preventDefault();
      openAndFocusSearch();
    } else if( event.key === "Escape" ) {
      setOpen( false );
    }
  };

  const loadSelectedOption = async ( value: PathValue<ControlFields, Path<ControlFields>> ) => {
    let selectedOption = null;

    if ( prefetchedSelectedOption && prefetchedSelectedOption.id === value ) {
      selectedOption = prefetchedSelectedOption;
    } else if( options.length === 0 ) {
      const options = await handleSearch( value as string );

      if( options.length > 0 ) selectedOption = options[0];
    } else selectedOption = options.find(( option ) => option.id === value ) ?? null;

    setSelectedOption( selectedOption );

    handleToClean();

    setOpen( false );
  };

  useEffect(() => {
    if( value ) loadSelectedOption( value );
    // loadSelectedOption mutates the local options cache; rerunning it for each
    // function identity change can refetch and reset the open state repeatedly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ value, prefetchedSelectedOption ]);

  return (
    <OutsideClick
      executeCallback={ open }
      callback={() => setOpen( false )}
    >
      <AutocompleteView
        $open={ open }
      >
        {
          label
            ? <label id={ labelId } className="input-label">{ label }</label>
            : <></>
        }

        <div className="field-view">
          <div
            className="trigger-button"
            onClick={() => setOpen(( open ) => !open )}
            onKeyDown={ handleTriggerKeyDown }
            id={ name }
            ref={ triggerRef }
            role="combobox"
            tabIndex={ 0 }
            aria-haspopup="listbox"
            aria-expanded={ open }
            aria-controls={ optionsId }
            aria-labelledby={ label ? labelId : undefined }
            aria-label={ label ? undefined : placeholder }
            aria-invalid={ Boolean( error )}
            aria-describedby={ error ? errorId : undefined }
          >
            {
              selectedOption
                ? <div className="selected-item">{ renderSelected( selectedOption ) }</div>
                : <span className="placeholder">{ placeholder }</span>
            }

            <motion.div
              aria-hidden={ selectedOption === null }
              animate={{
                opacity: selectedOption !== null ? 1 : 0,
                scale: selectedOption !== null ? 1 : 0,
              }}
              transition={{ duration: .2 }}
              className="icon-view"
            >
              <IconButton
                shape={ Shape.CIRCLE }
                color="text"
                onClick={ handleClearSelected }
                aria-label={ `Limpar ${ label ?? placeholder }` }
                tabIndex={ selectedOption ? 0 : -1 }
                style={{
                  width: 32,
                }}
              >
                <X
                  size={ 16 }
                />
              </IconButton>
            </motion.div>

            <motion.div
              animate={{
                transform: open ? "rotateZ(180deg)" : "rotateZ(0deg)",
              }}
              transition={{ duration: .2 }}
              className="icon-view"
            >
              <CaretDown
                size={ 16 }
              />
            </motion.div>
          </div>

          {
            open && (
              <div
                id={ optionsId }
                className="options-view"
                role="listbox"
                aria-label={ `Opções de ${ label ?? placeholder }` }
                aria-busy={ loading }
              >
                    <div className="input-search-view">
                      <input
                        ref={ searchInputRef }
                        type="text"
                        aria-label={ `Buscar ${ label ?? placeholder }` }
                        placeholder="Digite ao menos 3 caracteres para buscar"
                        value={ search }
                        id={ searchId }
                        autoFocus
                        autoComplete="off"
                        onKeyDown={( event ) => {
                          if( event.key !== "Escape" ) return;
                          event.preventDefault();
                          setOpen( false );
                          triggerRef.current?.focus();
                        }}
                        onChange={( e ) => {
                          handleSearch( e.target.value );

                          setSearch( e.target.value );
                        }}
                      />

                      <AnimatePresence>
                        {
                          search !== "" && (
                            <motion.div
                              initial={{
                                width: 0,
                              }}
                              animate={{
                                width: 32,
                              }}
                              exit={{
                                width: 0,
                              }}
                              transition={{ duration: .2 }}
                            >
                              <IconButton
                                shape={ Shape.CIRCLE }
                                color="text"
                                onClick={ handleToClean }
                                aria-label="Limpar busca"
                              >
                                <X
                                  size={ 16 }
                                />
                              </IconButton>
                            </motion.div>
                          )
                        }
                      </AnimatePresence>
                    </div>

            {
              loading
                ? (
                    <div className="full" role="status" aria-live="polite">
                      <SpinLoading
                        show={ true }
                      />

                      <span>Buscando...</span>
                    </div>
                  )
                : options.length === 0
                    ? (
                        <div className="full" role="status" aria-live="polite">
                          <span>Nenhum { label?.toLocaleLowerCase()} encontrado</span>
                        </div>
                      )
                    : (
                        <SimpleBar>
                          {
                            options.map(( option ) => (
                              <ItemView
                                key={ option.id }
                                className="item-view"
                                role="option"
                                aria-selected={ option.id === value }
                                onClick={() => {
                                  onChange( option.id );
                                  setOpen( false );
                                  triggerRef.current?.focus();
                                }}
                              >
                                { renderItem( option )}
                              </ItemView>
                            ))
                          }
                        </SimpleBar>
                      )
            }
              </div>
            )
          }
        </div>

        <span
          id={ errorId }
          className="input-error-message"
          role={ error ? "alert" : undefined }
        >
          { error?.message ?? ""}
        </span>
      </AutocompleteView>
    </OutsideClick>
  );
};
